import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  CircleNotch,
  Eye,
  Buildings,
  User,
  CaretDown,
  Prohibit,
  CheckCircle,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';

// ── Types ────────────────────────────────────────────────────────────────────

interface CustomerSummary {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  status: string;
  plan: string;
  workspaceType: string;
  provisioningStatus: string;
  trialEndsAt: string | null;
  userCount: number;
  activeUserCount: number;
  documentCount: number;
  storageBytes: number;
  createdAt: string;
  subscription: { status: string; expiresAt: string | null } | null;
}

interface CustomersResponse {
  data: CustomerSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(str: string | null) {
  if (!str) return null;
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatStorage(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function WorkspaceTypeBadge({ type }: { type: string }) {
  const isSolo = type === 'solo';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
        isSolo ? 'bg-plat-warning-soft text-plat-warning-text' : 'bg-plat-info-soft text-plat-info-text'
      }`}
    >
      {isSolo ? <User className="h-3 w-3" /> : <Buildings className="h-3 w-3" />}
      {isSolo ? 'Solo' : 'Chambers'}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    solo: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    professional: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text',
    trial: 'bg-plat-info-soft text-plat-info-text',
    suspended: 'bg-plat-warning-soft text-plat-warning-text',
    terminated: 'bg-plat-danger-soft text-plat-danger-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {status}
    </span>
  );
}

function ProvisioningBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-plat-success-soft px-2 py-0.5 text-xs font-medium text-plat-success-text">
        <CheckCircle className="h-3 w-3" />
        Active
      </span>
    );
  }
  if (status === 'provisioning') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-plat-info-soft px-2 py-0.5 text-xs font-medium text-plat-info-text">
        <CircleNotch className="h-3 w-3 animate-spin" />
        Provisioning
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-plat-danger-soft px-2 py-0.5 text-xs font-medium text-plat-danger-text">
        <Prohibit className="h-3 w-3" />
        Failed
      </span>
    );
  }
  // pending / unknown
  return (
    <span className="inline-flex items-center rounded-md bg-plat-neutral-soft px-2 py-0.5 text-xs font-medium capitalize text-plat-faint">
      {status}
    </span>
  );
}

// ── Inline plan change dropdown ───────────────────────────────────────────────

const PLANS = ['solo', 'starter', 'professional', 'enterprise'] as const;

function ChangePlanCell({ customer }: { customer: CustomerSummary }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: async (plan: string) => {
      await platformApi.post(`/tenants/${customer.id}/plan`, {
        plan,
        notes: 'Changed via platform console',
      });
    },
    onSuccess: (_data, plan) => {
      toast.success(`Plan changed to ${plan}`);
      queryClient.invalidateQueries({ queryKey: ['platform', 'customers'] });
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Failed to change plan'));
    },
  });

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={mutation.isPending}
        className="inline-flex items-center gap-1 rounded-lg border border-plat-border px-2.5 py-1 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
      >
        {mutation.isPending ? (
          <CircleNotch className="h-3 w-3 animate-spin" />
        ) : (
          <>
            Change plan
            <CaretDown className="h-3 w-3" />
          </>
        )}
      </button>
      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-36 overflow-hidden rounded-lg border border-plat-border bg-plat-surface shadow-xl">
            {PLANS.map((p) => (
              <button
                key={p}
                onClick={() => {
                  setOpen(false);
                  if (p !== customer.plan) mutation.mutate(p);
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-xs hover:bg-plat-hover ${
                  p === customer.plan ? 'text-plat-info-text' : 'text-plat-secondary'
                }`}
              >
                <span className="capitalize">{p}</span>
                {p === customer.plan && <span className="text-[10px] text-plat-faint">current</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── SuspendActivateButton ─────────────────────────────────────────────────────

function SuspendActivateButton({ customer }: { customer: CustomerSummary }) {
  const queryClient = useQueryClient();
  const isSuspended = customer.status === 'suspended';

  const mutation = useMutation({
    mutationFn: async () => {
      const endpoint = isSuspended
        ? `/tenants/${customer.id}/activate`
        : `/tenants/${customer.id}/suspend`;
      await platformApi.post(endpoint, {});
    },
    onSuccess: () => {
      toast.success(isSuspended ? 'Customer activated' : 'Customer suspended');
      queryClient.invalidateQueries({ queryKey: ['platform', 'customers'] });
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Action failed'));
    },
  });

  if (customer.status === 'terminated') return null;

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:text-plat-disabled ${
        isSuspended
          ? 'border-plat-success-line text-plat-success-text hover:border-plat-success-line hover:text-plat-success-text'
          : 'border-plat-warning-line text-plat-warning-text hover:border-plat-warning-line hover:text-plat-warning-text'
      }`}
    >
      {mutation.isPending ? (
        <CircleNotch className="h-3 w-3 animate-spin" />
      ) : isSuspended ? (
        <>
          <CheckCircle className="h-3 w-3" />
          Activate
        </>
      ) : (
        <>
          <Prohibit className="h-3 w-3" />
          Suspend
        </>
      )}
    </button>
  );
}

// ── Tab config ────────────────────────────────────────────────────────────────

const TABS = [
  { label: 'All', value: '' },
  { label: 'Chambers', value: 'chambers' },
  { label: 'Solo Barristers', value: 'solo' },
] as const;

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Trial', value: 'trial' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Terminated', value: 'terminated' },
] as const;

// ── CustomersPage ─────────────────────────────────────────────────────────────

export default function CustomersPage() {
  const [activeTab, setActiveTab] = useState<'' | 'chambers' | 'solo'>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search
  const debounce = useCallback(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const cleanup = debounce();
    return cleanup;
  }, [debounce]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, activeTab]);

  const { data, isLoading, error } = useQuery<CustomersResponse>({
    queryKey: ['platform', 'customers', activeTab, statusFilter, debouncedSearch, page],
    queryFn: async () => {
      const res = await platformApi.get('/tenants/customers', {
        params: {
          workspaceType: activeTab || undefined,
          status: statusFilter || undefined,
          search: debouncedSearch || undefined,
          page,
          limit: 25,
        },
      });
      // listCustomers returns { data, pagination } and the controller sends it unwrapped.
      return res.data;
    },
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-plat-border pb-0">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2.5 text-sm font-medium transition ${
              activeTab === tab.value
                ? 'border-b-2 border-plat-accent-solid text-plat-info-text'
                : 'text-plat-muted hover:text-plat-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-plat-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, slug…"
            className="h-9 w-full rounded-lg border border-plat-border bg-plat-surface pl-9 pr-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
          {getPlatformApiErrorMessage(error)}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Workspace</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Provisioning</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Users</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Storage</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Trial ends</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-sm text-plat-muted">
                      No customers found.
                    </td>
                  </tr>
                ) : rows.map((c) => {
                  const trialDate = c.trialEndsAt ? formatDate(c.trialEndsAt) : null;
                  const trialPast = c.trialEndsAt ? new Date(c.trialEndsAt) < new Date() : false;
                  return (
                    <tr key={c.id} className="transition hover:bg-plat-hover">
                      {/* Workspace */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div>
                            <Link
                              to={`/platform/tenants/${c.id}`}
                              className="font-medium text-plat-fg hover:text-plat-info-text hover:underline"
                            >
                              {c.name}
                            </Link>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span className="text-xs text-plat-muted">{c.slug}</span>
                              <WorkspaceTypeBadge type={c.workspaceType} />
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Plan */}
                      <td className="px-5 py-3.5"><PlanBadge plan={c.plan} /></td>
                      {/* Status */}
                      <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                      {/* Provisioning */}
                      <td className="px-5 py-3.5"><ProvisioningBadge status={c.provisioningStatus} /></td>
                      {/* Users */}
                      <td className="px-5 py-3.5 text-plat-secondary">
                        {c.userCount}
                        {c.activeUserCount !== c.userCount && (
                          <span className="ml-1 text-xs text-plat-faint">({c.activeUserCount} active)</span>
                        )}
                      </td>
                      {/* Storage */}
                      <td className="px-5 py-3.5 text-plat-secondary">{formatStorage(c.storageBytes)}</td>
                      {/* Trial ends */}
                      <td className="px-5 py-3.5">
                        {trialDate ? (
                          <span className={trialPast ? 'text-plat-danger-text' : 'text-plat-secondary'}>
                            {trialDate}
                          </span>
                        ) : (
                          <span className="text-plat-faint">—</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/platform/tenants/${c.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-plat-info-text hover:text-plat-info-text"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Link>
                          <ChangePlanCell customer={c} />
                          <SuspendActivateButton customer={c} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-plat-muted">
                Page {data.pagination.page} of {data.pagination.totalPages} — {data.pagination.total} total
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Previous
                </button>
                <button
                  disabled={page >= data.pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
